import type { RuleViolation } from '@billing/validation/rules';
import type { UseFormReturnType } from '@mantine/form';
import type { Claim } from 'interfaces';
import { useEffect, useRef } from 'react';

type RuleViolationForm = Pick<
  UseFormReturnType<Claim>,
  'clearFieldError' | 'errors' | 'setFieldError'
>;

const getRuleFormErrors = (
  violations: RuleViolation[]
): Record<string, string> => {
  const messagesByField = new Map<string, Set<string>>();

  violations
    .filter((violation) => violation.severity === 'block')
    .forEach((violation) => {
      violation.formFields?.forEach((field) => {
        const messages = messagesByField.get(field) || new Set<string>();
        messages.add(violation.message);
        messagesByField.set(field, messages);
      });
    });

  return Object.fromEntries(
    Array.from(messagesByField, ([field, messages]) => [
      field,
      Array.from(messages).join(' '),
    ])
  );
};

export const useRuleViolationFormErrors = (
  form: RuleViolationForm,
  violations: RuleViolation[]
): void => {
  const appliedErrorsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const nextErrors = getRuleFormErrors(violations);

    Object.entries(appliedErrorsRef.current).forEach(([field, message]) => {
      if (!nextErrors[field] && form.errors[field] === message) {
        form.clearFieldError(field);
      }
    });

    Object.entries(nextErrors).forEach(([field, message]) => {
      const previousRuleMessage = appliedErrorsRef.current[field];
      const existingError = form.errors[field];
      if (existingError && existingError !== previousRuleMessage) return;
      if (existingError !== message) form.setFieldError(field, message);
    });

    appliedErrorsRef.current = nextErrors;
  }, [form, form.errors, violations]);
};
